import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Briefcase, Users, Clock, Shield, ArrowRight, Building2 } from "lucide-react";
import { CHAIR_MASSAGE } from "@/data/extras";
import { THERAPIST } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";

export default function ChairMassage() {
    useSEO({
        title: "Massage entreprise sur chaise à Blainville · Jérémy Tessier",
        description: "Massage sur chaise en entreprise dans la Rive-Nord — 90 $/heure. Séances de 15, 30, 45 ou 60 min. Reçu d'assurance fourni. Bien-être au travail.",
        canonical: "https://massoclic.ca/massage-entreprise",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Massage entreprise sur chaise",
            provider: {
                "@type": "HealthAndBeautyBusiness",
                name: "Jérémy Tessier — Massothérapie",
                telephone: THERAPIST.phone,
                url: "https://massoclic.ca",
            },
            areaServed: "Rive-Nord de Montréal",
            offers: CHAIR_MASSAGE.options.map((o) => ({
                "@type": "Offer",
                price: o.price,
                priceCurrency: "CAD",
                name: `Massage sur chaise ${o.duration} min`,
                availability: "https://schema.org/InStock",
            })),
        },
    });

    const mailSubject = encodeURIComponent("Demande — Massage entreprise sur chaise");
    const mailBody = encodeURIComponent(
        "Bonjour Jérémy,\n\nJe souhaite organiser des massages sur chaise en entreprise. Détails :\n\n• Nom de l'entreprise : \n• Adresse : \n• Nombre d'employés estimé : \n• Durée souhaitée par personne (15/30/45/60 min) : \n• Date(s) envisagée(s) : \n\nMerci !"
    );

    return (
        <main data-testid="chair-massage-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                {/* Hero */}
                <div className="max-w-3xl mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Briefcase size={14} className="text-[#3D8C95]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Bien-être en entreprise
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Massage sur{" "}
                        <span className="italic text-[#3D8C95]">chaise</span> en entreprise
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed">
                        Offrez à vos employés un moment de détente sans quitter le bureau.
                        Je me déplace avec une chaise ergonomique professionnelle dans votre entreprise sur la Rive-Nord.
                        Idéal pour le bien-être au travail, les événements RH et la reconnaissance d'équipe.
                    </p>
                    <p className="mt-4 text-sm text-[#3D8C95] italic font-medium">
                        Reçu d&apos;assurance fourni pour chaque participant.
                    </p>
                    <div className="mt-6">
                        <ReserveButton
                            mailSubject="Massage entreprise sur chaise — Demande"
                            mailBody={"Bonjour Jérémy,\n\nJe souhaite organiser des massages sur chaise en entreprise.\n\n• Nom de l'entreprise : \n• Adresse : \n• Nombre d'employés estimé : \n• Durée souhaitée par personne (15/30/45/60 min) : \n• Date(s) envisagée(s) : \n\nMerci !"}
                            quoteSubject="Devis — Massage entreprise"
                            quoteBody={"Bonjour Jérémy,\n\nJe souhaite un devis pour un massage entreprise sur chaise.\n\n• Nom de l'entreprise : \n• Adresse : \n• Nombre d'employés estimé : \n• Durée par personne : \n• Date envisagée : \n\nMerci !"}
                        />
                    </div>
                </div>

                {/* Tarifs */}
                <section className="mb-14">
                    <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29] mb-2">Tarifs</h2>
                    <p className="text-[#6E6862] mb-8">
                        <b>{CHAIR_MASSAGE.hourlyRate} $ de l&apos;heure</b> · fractionnable en séances plus courtes selon vos besoins.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {CHAIR_MASSAGE.options.map((o) => (
                            <div
                                key={o.id}
                                data-testid={`chair-option-${o.duration}`}
                                className={`rounded-3xl p-6 md:p-7 border transition-all hover:-translate-y-1 ${
                                    o.duration === 30
                                        ? "bg-[#3D8C95] text-white border-[#3D8C95] shadow-[0_20px_50px_rgba(61,140,149,0.35)]"
                                        : "bg-white border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)]"
                                }`}
                            >
                                <Clock size={20} className={o.duration === 30 ? "text-white/85" : "text-[#3D8C95]"} />
                                <div className="mt-3 font-display text-4xl">
                                    {o.duration}
                                    <span className={`text-sm ${o.duration === 30 ? "text-white/80" : "text-[#6E6862]"}`}> min</span>
                                </div>
                                <div className={`mt-4 pt-4 border-t ${o.duration === 30 ? "border-white/20" : "border-[#E8E2D9]"}`}>
                                    <span className="font-display text-3xl">{o.price}</span>
                                    <span className={`text-sm ${o.duration === 30 ? "text-white/80" : "text-[#6E6862]"}`}> $ / personne</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Bénéfices entreprise */}
                <section className="mb-14 grid md:grid-cols-3 gap-6">
                    {[
                        { icon: Users, title: "Renforce l'équipe", desc: "Un geste tangible de reconnaissance envers vos employés." },
                        { icon: Building2, title: "Zéro logistique", desc: "Je me déplace avec ma chaise. Vous fournissez juste un coin tranquille." },
                        { icon: Shield, title: "Reçus d'assurance", desc: "Chaque participant reçoit un reçu de massothérapie remboursable." },
                    ].map((f, i) => (
                        <div key={i} className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <div className="w-12 h-12 rounded-2xl bg-[#3D8C95]/10 flex items-center justify-center mb-4">
                                <f.icon size={22} className="text-[#3D8C95]" />
                            </div>
                            <h3 className="font-display text-xl text-[#2C2A29]">{f.title}</h3>
                            <p className="mt-2 text-sm text-[#6E6862] leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </section>

                {/* CTA */}
                <div className="rounded-3xl bg-[#2C2A29] text-white p-8 md:p-12 text-center">
                    <h2 className="font-display text-3xl md:text-4xl leading-tight">
                        Organisons ça ensemble
                    </h2>
                    <p className="mt-3 text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Envoyez-moi vos détails (nombre d&apos;employés, durée, date) — je vous prépare une proposition dans les 24 h.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a
                            href={`mailto:${THERAPIST.email}?subject=${mailSubject}&body=${mailBody}`}
                            data-testid="chair-mail-cta"
                        >
                            <Button className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base">
                                Demander un devis <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </a>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6 text-base">
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>

                <div className="mt-10 text-center">
                    <Link to="/" className="text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                        ← Retour à l&apos;accueil
                    </Link>
                </div>
            </div>
        </main>
    );
}
